import React, { useState } from 'react'
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table'

import { Box, Pagination, Scroller } from '@/elements'
import { TableBody, TableFoot, TableHead, TableNoData } from './components'
import { transformFilterData } from './helpers'
import { TableElement } from './styled'
import { useDidMountEffect } from '@hooks'
import { Color, isEmpty } from '@/utils'

export const TABLE_WIDTH_NUMBER_MODIFIER = 1000
export const TABLE_WIDTH_PERCENTAGE_MODIFIER = 1000000000
export const TABLE_PLACEHOLDER = 'placeholder'

export const TableV2 = ({
    data,
    noDataText = 'No data',
    columns = [],
    whiteSpace,
    align,

    getHeaderProps = () => {},
    getFooterProps = () => {},
    getColumnProps = () => ({}),
    getRowProps = () => {},
    getCellProps = () => ({}),
    onClickRow,

    // Skeleton props
    loading = false,
    skeletonRows = 10,
    skeletonHeight = '18px',

    // Pagination props
    page = 1,
    size = 10,
    totalCount = 0,
    totalPages = 1,
    onPageChange,

    // Sorting props
    onSortingChange,
    initialStateSort,

    // Filters props
    onFiltersChange,
    initialStateFilter = [],
    getRowCanExpand,
    renderSubComponent,
}) => {
    // sortingState
    const [sorting, setSorting] = useState([{ ...initialStateSort }])

    // filtersTate
    const [columnFilters, setColumnFilters] = useState(
        transformFilterData(initialStateFilter),
    )
    const [expanded, setExpanded] = React.useState({})

    useDidMountEffect(() => {
        if (onSortingChange) {
            onSortingChange(sorting)
        }
    }, [sorting])

    useDidMountEffect(() => {
        if (onFiltersChange) {
            const filterKeys = columns
                .filter(item => item.enableColumnFilter)
                .map(item => item.id)
            if (filterKeys.length) {
                const updatedFilters = !filterKeys
                    ? []
                    : filterKeys.map(key => ({
                        id: key,
                        value:
                            columnFilters.find(filter => filter.id === key)?.value || '',
                    }))

                onFiltersChange(updatedFilters)
            }
        }
    }, [columnFilters])

    const columnHelper = createColumnHelper()

    const getTableColumnData = (column) => ({
        id: column.id || column.body || TABLE_PLACEHOLDER,
        // Header needs to be set no matter what
        header: column.header,

        enableColumnFilter: column.enableColumnFilter || false,
        enableSorting: column.enableSorting || false,

        // Custom cell if body is not string (id)
        ...(typeof column.body === 'function' && { cell: column.body }),

        footer: column.footer,

        columns: column.columns?.map(column => getTableColumn(column)),

        // Placeholder boolean for headers
        isPlaceholder: column.header === undefined,

        // Custom size to bypass tanstack column size weirdness
        size: typeof column.width === 'number' ? `${column.width}px` : column.width,
        minSize:
            typeof column.minWidth === 'number'
                ? `${column.minWidth}px`
                : column.minWidth,
        meta: column.meta,
    })

    const getTableColumn = (column) =>
        column.columns
            ? columnHelper.group(getTableColumnData(column))
            : columnHelper.accessor(
                column.id || column.body,
                getTableColumnData(column),
            )

    const table = useReactTable({
        data,
        columns: columns.map(column => getTableColumn(column)),
        state: {
            sorting,
            columnFilters,
            expanded,
        },
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting,
        onExpandedChange: setExpanded,
        manualSorting: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        enableSorting: Boolean(onSortingChange),

        debugTable: false,
        debugHeaders: false,
        debugColumns: false,

        getRowCanExpand,
        getExpandedRowModel: getExpandedRowModel(),
        autoResetExpanded: true,
    })

    // Cell props with general table props
    const getCombinedCellProps = (cell) => ({
        ...(whiteSpace && { whiteSpace }),
        ...(align && { align }),
        ...getCellProps(cell),
    })

    // Get original column definition by id
    const getColumnById = (data, id) => {
        for (const column of Object.values(data)) {
            for (const key of Object.keys(column)) {
                if ((key === 'id' || key === 'body') && column[key] === id) {
                    return column
                }

                if (key === 'columns') {
                    const nested = getColumnById(column['columns'], id)
                    if (nested) {
                        return nested
                    }
                }
            }
        }
    }

    // Column props with custom column definitions props
    const getCombinedColumnProps = (id, columnTable) => {
        const column = getColumnById(columns, id)

        return {
            justify: column?.justify,
            headerProps: column?.headerProps,
            footerProps: column?.footerProps,
            ...getColumnProps(columnTable),
        }
    }

    return (
        <Box width="100%" direction="column" gap="10px" $minHeight="0">
            <Scroller
                padding="0"
                $borderColor={Color.opacitySkyHover}
                $outerOverflowHidden={false}
            >
                <TableElement>
                    <TableHead
                        table={table}
                        getHeaderProps={getHeaderProps}
                        getColumnProps={getCombinedColumnProps}
                        getRowProps={getRowProps}
                    />

                    <TableBody
                        table={table}
                        getCellProps={getCombinedCellProps}
                        getColumnProps={getCombinedColumnProps}
                        getRowProps={getRowProps}
                        onClickRow={onClickRow}
                        loading={loading}
                        skeletonRows={size || skeletonRows}
                        skeletonHeight={skeletonHeight}
                        renderSubComponent={renderSubComponent}
                    />

                    <TableFoot
                        table={table}
                        getRowProps={getRowProps}
                        getColumnProps={getCombinedColumnProps}
                        getFooterProps={getFooterProps}
                        getCellProps={getCombinedCellProps}
                    />
                </TableElement>
            </Scroller>

            {!isEmpty(data) && onPageChange && (
                <Pagination
                    currentPage={page}
                    totalCount={totalCount}
                    totalPages={totalPages}
                    size={size}
                    handleChange={onPageChange}
                />
            )}

            {isEmpty(data) && <TableNoData text={noDataText} />}
        </Box>
    )
}

export * from './components/Expander'